import type {
  MultiCampaignUploadResult,
  PaginatedResponse,
  Ticket,
  TicketFilters,
  TicketStatus,
} from '@/types'
import { apiClient } from './client'
import { getApiErrorMessage } from './errors'
import { t } from '@/i18n'

interface BackendTicket {
  id: string
  batchId: string
  pharmacyId: string
  challengeId: string
  status: TicketStatus
  baseAmountCents: number | null
  finalAmountCents: number | null
  currency: string
  /** Rejection reason code; only non-null when status is 'declined'. */
  declineReasonCode: string | null
  reviewedAt: string | null
  reviewedByUserId: string | null
  createdAt: string
  updatedAt: string
}

interface BackendPaginatedTickets {
  data: BackendTicket[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface BackendTicketCounts {
  approved: number
  in_review: number
  declined: number
  total: number
}

interface BackendImageAccessResponse {
  downloadUrl: string
  expiresAt: string
}

interface BackendTicketBatchResult {
  batchId: string
  tickets: BackendTicket[]
  successCount: number
  failedChallengeIds: string[]
}

// Built per call so the fallback label matches the active language.
function receiptPlaceholder(): string {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="600" viewBox="0 0 400 600">
    <rect width="400" height="600" fill="#F3F4F6"/>
    <rect x="60" y="80" width="280" height="40" rx="8" fill="#E5E7EB"/>
    <rect x="60" y="140" width="220" height="24" rx="6" fill="#E5E7EB"/>
    <rect x="60" y="190" width="280" height="18" rx="6" fill="#E5E7EB"/>
    <rect x="60" y="220" width="260" height="18" rx="6" fill="#E5E7EB"/>
    <rect x="60" y="250" width="240" height="18" rx="6" fill="#E5E7EB"/>
    <rect x="60" y="300" width="280" height="1" fill="#D1D5DB"/>
    <rect x="60" y="330" width="160" height="18" rx="6" fill="#E5E7EB"/>
    <rect x="60" y="360" width="200" height="18" rx="6" fill="#E5E7EB"/>
    <rect x="60" y="410" width="120" height="18" rx="6" fill="#E5E7EB"/>
    <rect x="60" y="440" width="220" height="18" rx="6" fill="#E5E7EB"/>
    <text x="200" y="520" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" fill="#9CA3AF">${t('tickets.receiptPlaceholder')}</text>
  </svg>`
  )}`
}

type UrlCacheEntry = { url: string; expiresAt: number }
const thumbnailUrlCache = new Map<string, UrlCacheEntry>()
const imageUrlCache = new Map<string, UrlCacheEntry>()

function isValidTicketImageUrl(url: string | null | undefined): url is string {
  if (!url) return false

  const trimmed = url.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('data:image/')) return true

  try {
    const parsed = new URL(trimmed)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

function centsToEur(value: number | null): number | undefined {
  if (value === null) return undefined
  return value / 100
}

function toTicket(ticket: BackendTicket): Ticket {
  return {
    id: ticket.id,
    imageUrl: receiptPlaceholder(),
    thumbnailUrl: receiptPlaceholder(),
    status: ticket.status,
    challengeId: ticket.challengeId,
    pharmacistId: ticket.pharmacyId,
    uploadDate: new Date(ticket.createdAt),
    processedDate: ticket.reviewedAt ? new Date(ticket.reviewedAt) : undefined,
    amount: centsToEur(ticket.finalAmountCents),
    declineReasonCode: ticket.declineReasonCode ?? undefined,
    batchId: ticket.batchId,
  }
}

async function getCachedUrl(
  cache: Map<string, UrlCacheEntry>,
  key: string,
  fetcher: () => Promise<BackendImageAccessResponse>,
  forceRefresh: boolean = false
): Promise<string> {
  const cached = cache.get(key)
  if (!forceRefresh && cached && cached.expiresAt > Date.now() + 5_000) return cached.url

  const response = await fetcher()
  if (!isValidTicketImageUrl(response.downloadUrl)) {
    throw new Error('Invalid ticket image URL')
  }
  const expires = new Date(response.expiresAt).getTime()
  cache.set(key, { url: response.downloadUrl, expiresAt: expires })
  return response.downloadUrl
}

async function getTicketThumbnailUrl(ticketId: string, forceRefresh: boolean = false): Promise<string> {
  return getCachedUrl(thumbnailUrlCache, ticketId, async () => {
    const response = await apiClient.get<BackendImageAccessResponse>(
      `/api/v1/tickets/${ticketId}/thumbnail`
    )
    return response.data
  }, forceRefresh)
}

async function getTicketImageUrl(ticketId: string, forceRefresh: boolean = false): Promise<string> {
  return getCachedUrl(imageUrlCache, ticketId, async () => {
    const response = await apiClient.get<BackendImageAccessResponse>(
      `/api/v1/tickets/${ticketId}/image`
    )
    return response.data
  }, forceRefresh)
}

async function attachTicketImages(ticket: Ticket): Promise<Ticket> {
  const [thumbnailResult, imageResult] = await Promise.allSettled([
    getTicketThumbnailUrl(ticket.id),
    getTicketImageUrl(ticket.id),
  ])

  const imageUrl = imageResult.status === 'fulfilled' ? imageResult.value : receiptPlaceholder()
  const thumbnailUrl =
    thumbnailResult.status === 'fulfilled'
      ? thumbnailResult.value
      : imageResult.status === 'fulfilled'
        ? imageResult.value
        : receiptPlaceholder()

  return {
    ...ticket,
    thumbnailUrl,
    imageUrl,
  }
}

export const ticketService = {
  getReceiptPlaceholderUrl(): string {
    return receiptPlaceholder()
  },

  isTicketImageUrlValid(url: string | null | undefined): url is string {
    return isValidTicketImageUrl(url)
  },

  async getTickets(
    _pharmacistId: string,
    filters?: TicketFilters,
    page: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedResponse<Ticket>> {
    try {
      const response = await apiClient.get<BackendPaginatedTickets>('/api/v1/tickets', {
        params: {
          status: filters?.status && filters.status !== 'all' ? filters.status : undefined,
          challengeId: filters?.challengeId,
          dateFrom: filters?.dateFrom?.toISOString(),
          dateTo: filters?.dateTo?.toISOString(),
          page,
          pageSize,
        },
      })

      const tickets = response.data.data.map(toTicket)
      const withImages = await Promise.all(tickets.map(attachTicketImages))

      return {
        data: withImages,
        total: response.data.total,
        page: response.data.page,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
      }
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadTickets')))
    }
  },

  async getTicket(id: string): Promise<Ticket | null> {
    try {
      const response = await apiClient.get<BackendTicket>(`/api/v1/tickets/${id}`)
      return attachTicketImages(toTicket(response.data))
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadTicket')))
    }
  },

  async getTicketsByStatus(_pharmacistId: string, status: TicketStatus): Promise<Ticket[]> {
    const response = await this.getTickets('', { status, challengeId: undefined }, 1, 100)
    return response.data
  },

  async getTicketCounts(_pharmacistId: string): Promise<Record<TicketStatus | 'total', number>> {
    try {
      const response = await apiClient.get<BackendTicketCounts>('/api/v1/tickets/counts')
      return response.data
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadTicketSummary')))
    }
  },

  async getRecentTickets(_pharmacistId: string, limit: number = 5): Promise<Ticket[]> {
    try {
      const response = await apiClient.get<BackendTicket[]>('/api/v1/tickets/recent', {
        params: { limit },
      })
      const tickets = response.data.map(toTicket)
      return Promise.all(tickets.map(attachTicketImages))
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadRecentTickets')))
    }
  },

  async uploadTicket(
    challengeId: string,
    _challengeTitle: string,
    _pharmacistId: string,
    _pharmacistName: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Ticket> {
    const result = await this.uploadTicketBatch([challengeId], file, onProgress)
    const first = result.tickets[0]
    if (!first) {
      throw new Error(t('errors.noTicketCreated'))
    }
    return first
  },

  async uploadTicketBatch(
    challengeIds: string[],
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<MultiCampaignUploadResult> {
    try {
      if (challengeIds.length === 0) {
        throw new Error(t('errors.selectChallenge'))
      }

      const form = new FormData()
      form.append('file', file)
      challengeIds.forEach(id => form.append('challengeIds', id))

      const created = await apiClient.post<BackendTicketBatchResult>('/api/v1/ticket-batches', form, {
        onUploadProgress: (event) => {
          if (!event.total) return
          const progress = event.loaded >= event.total ? 100 : Math.round((event.loaded / event.total) * 100)
          onProgress?.(progress)
        },
      })

      const tickets = created.data.tickets.map(toTicket)
      const withImages = await Promise.all(tickets.map(attachTicketImages))

      return {
        batchId: created.data.batchId,
        tickets: withImages,
        successCount: created.data.successCount,
        failedChallengeIds: created.data.failedChallengeIds,
      }
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.uploadTicket')))
    }
  },

  async getTicketBatch(batchId: string): Promise<MultiCampaignUploadResult> {
    try {
      const response = await apiClient.get<BackendTicketBatchResult>(`/api/v1/ticket-batches/${batchId}`)
      const tickets = response.data.tickets.map(toTicket)
      const withImages = await Promise.all(tickets.map(attachTicketImages))

      return {
        batchId: response.data.batchId,
        tickets: withImages,
        successCount: response.data.successCount,
        failedChallengeIds: response.data.failedChallengeIds,
      }
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadTicketBatch')))
    }
  },

  async refreshTicketImageUrls(ticketId: string): Promise<{ thumbnailUrl?: string; imageUrl?: string }> {
    const [thumbnailResult, imageResult] = await Promise.allSettled([
      getTicketThumbnailUrl(ticketId, true),
      getTicketImageUrl(ticketId, true),
    ])

    return {
      thumbnailUrl: thumbnailResult.status === 'fulfilled' ? thumbnailResult.value : undefined,
      imageUrl: imageResult.status === 'fulfilled' ? imageResult.value : undefined,
    }
  },
}
